import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import { getPepProfile, savePepProfile, type PepProfile } from '@/services/pepProfile';
import { API_CONFIG, fetchAPI } from '@/services/api';

export type PepProfileV2 = PepProfile & {
  integrations?: string[];
  focusTags?: string[];
  customApiNotes?: string;
  fieldEditedAt?: Record<string, string>;
};

const INTEGRATION_OPTIONS = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'substack', label: 'Substack' },
  { key: 'fireflies', label: 'Fireflies' },
  { key: 'gohighlevel', label: 'GoHighLevel CRM' },
  { key: 'custom_api', label: 'Custom API Integration' },
] as const;

const PROFILE_FIELDS: Array<{ key: keyof PepProfile; label: string; hint: string }> = [
  { key: 'brandVoice', label: 'Brand Voice', hint: 'How should PEPai sound when representing you? Describe your tone, style, and communication personality.' },
  { key: 'icpPersona', label: 'ICP / Persona', hint: 'Who is your ideal customer? Describe titles, industries, company size, pain points \u2014 the more specific, the better PEPai targets.' },
  { key: 'offersProducts', label: 'Offers / Products', hint: 'What do you sell? List your products, services, and key value propositions PEPai should reference.' },
  { key: 'channelConstraints', label: 'Channel Constraints', hint: 'Which channels should PEPai prioritize or avoid? E.g. "Email-first, no cold calling, LinkedIn for warm intros only."' },
  { key: 'languageDos', label: 'Language \u2014 Do This', hint: 'What language, phrases, or approaches should PEPai always use? E.g. "Reference ROI, use case studies, be concise."' },
  { key: 'languageDonts', label: 'Language \u2014 Never Do This', hint: 'What should PEPai never say? E.g. "No generic hype, no fabricated claims, never use the word synergy."' },
  { key: 'sequencePolicy', label: 'Sequence Policy', hint: 'How should PEPai build outreach sequences? Fixed scripts or adaptive next-best actions based on signals?' },
  { key: 'touchpointRules', label: 'Touchpoint Rules', hint: 'Rules for touchpoint generation. E.g. "First 5 touchpoints with channel, action, angle, and wait_days."' },
];

const EMPTY_PROFILE: PepProfileV2 = {
  brandVoice: '',
  icpPersona: '',
  offersProducts: '',
  languageDos: '',
  languageDonts: '',
  channelConstraints: '',
  sequencePolicy: '',
  touchpointRules: '',
  approvalMode: 'manual',
  integrations: [],
  focusTags: [],
  customApiNotes: '',
  fieldEditedAt: {},
};

function formatEditDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

async function sendProfileEmail(userEmail: string, profile: PepProfileV2): Promise<void> {
  try {
    await fetchAPI(API_CONFIG.USERS_API, {
      method: 'POST',
      body: JSON.stringify({
        action: 'send_pep_profile_email',
        email: userEmail,
        profile,
      }),
    });
  } catch {
    // non-blocking
  }
}

type Props = {
  userEmail: string;
  disabled?: boolean;
  locked?: boolean;
};

export function PepProfileEditorV2({ userEmail, disabled = false, locked = false }: Props) {
  const [profile, setProfile] = useState<PepProfileV2>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');

  const canSave = !disabled && !locked && !saving && !!userEmail;

  useEffect(() => {
    if (!userEmail) return;
    setLoading(true);
    getPepProfile(userEmail)
      .then((res) => {
        if (res.profile) {
          const p = res.profile as PepProfileV2;
          setProfile({
            ...EMPTY_PROFILE,
            ...res.profile,
            integrations: p.integrations ?? [],
            focusTags: p.focusTags ?? [],
            customApiNotes: p.customApiNotes ?? '',
            fieldEditedAt: p.fieldEditedAt ?? {},
          });
        }
      })
      .catch((err: any) => setError(err?.message || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [userEmail]);

  const setField = useCallback((key: string, value: string) => {
    const now = new Date().toISOString();
    setProfile((prev) => ({
      ...prev,
      [key]: value,
      fieldEditedAt: { ...(prev.fieldEditedAt ?? {}), [key]: now },
    }));
  }, []);

  const toggleIntegration = useCallback((key: string) => {
    const now = new Date().toISOString();
    setProfile((prev) => {
      const current = prev.integrations ?? [];
      const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      return { ...prev, integrations: next, fieldEditedAt: { ...(prev.fieldEditedAt ?? {}), integrations: now } };
    });
  }, []);

  const addTag = useCallback((raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    const now = new Date().toISOString();
    setProfile((prev) => {
      const current = prev.focusTags ?? [];
      if (current.some((t) => t.toLowerCase() === tag.toLowerCase())) return prev;
      return { ...prev, focusTags: [...current, tag], fieldEditedAt: { ...(prev.fieldEditedAt ?? {}), focusTags: now } };
    });
    setTagInput('');
  }, []);

  const removeTag = useCallback((tag: string) => {
    const now = new Date().toISOString();
    setProfile((prev) => ({
      ...prev,
      focusTags: (prev.focusTags ?? []).filter((t) => t !== tag),
      fieldEditedAt: { ...(prev.fieldEditedAt ?? {}), focusTags: now },
    }));
  }, []);

  const handleTagKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  }, [tagInput, addTag]);

  const onSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await savePepProfile(userEmail, profile as PepProfile);
      if (res.success) {
        setNotice('Profile saved');
        await sendProfileEmail(userEmail, profile);
        setTimeout(() => setNotice(''), 4000);
      } else {
        setError(res.error || 'Save failed');
      }
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [canSave, userEmail, profile]);

  const isDisabled = disabled || locked;
  const editedAt = profile.fieldEditedAt ?? {};

  return (
    <div className="space-y-6">
      {/* Integrations */}
      <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-white">Connected Platforms</h3>
          <p className="text-xs text-gray-400 mt-1">Select the platforms you use. We will configure PEPai and onboarding around your stack.</p>
          {editedAt.integrations && <p className="text-[10px] text-gray-500 mt-1">Last updated: {formatEditDate(editedAt.integrations)}</p>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {INTEGRATION_OPTIONS.map((opt) => {
            const checked = (profile.integrations ?? []).includes(opt.key);
            return (
              <label
                key={opt.key}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  checked ? 'bg-red-600/10 border-red-500/40 text-white' : 'bg-gray-900/30 border-gray-700 text-gray-400 hover:border-gray-500'
                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input type="checkbox" checked={checked} disabled={isDisabled} onChange={() => toggleIntegration(opt.key)} className="w-3.5 h-3.5 accent-red-600" />
                {opt.label}
              </label>
            );
          })}
        </div>
        {(profile.integrations ?? []).includes('custom_api') && (
          <div className="mt-3">
            <label className="block">
              <div className="text-xs text-gray-400 mb-1">Custom API Details</div>
              <textarea
                className="w-full min-h-[80px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600"
                placeholder="API name, documentation URL, and any keys or notes for setup..."
                value={profile.customApiNotes ?? ''}
                disabled={isDisabled}
                onChange={(e) => setField('customApiNotes', e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      {/* Focus Tags */}
      <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-white">Focus Tags</h3>
          <p className="text-xs text-gray-400 mt-1">Add titles, industries, verticals, or priorities that define your focus. PEPai uses these for scoring, algorithms, and onboarding.</p>
          {editedAt.focusTags && <p className="text-[10px] text-gray-500 mt-1">Last updated: {formatEditDate(editedAt.focusTags)}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {(profile.focusTags ?? []).map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-gray-800 border border-gray-600 text-gray-200">
              {tag}
              {!isDisabled && (
                <button onClick={() => removeTag(tag)} className="text-gray-400 hover:text-red-400 ml-0.5" aria-label={`Remove ${tag}`}>\u00d7</button>
              )}
            </span>
          ))}
        </div>
        {!isDisabled && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600"
              placeholder="Type a tag and press Enter (e.g. SaaS, VP Sales, Healthcare)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
            />
            <button onClick={() => addTag(tagInput)} className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-xs text-white">Add</button>
          </div>
        )}
      </div>

      {/* Profile Fields */}
      <div className="bg-card-dark border border-border-dark rounded-xl p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {PROFILE_FIELDS.map((field) => (
          <label key={field.key} className="block">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-gray-400">{field.label}</span>
              {editedAt[field.key] && <span className="text-[10px] text-gray-500">{formatEditDate(editedAt[field.key])}</span>}
            </div>
            <p className="text-[11px] text-gray-500 mb-2">{field.hint}</p>
            <textarea
              className="w-full min-h-[110px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600"
              value={(profile as any)[field.key] ?? ''}
              disabled={isDisabled}
              onChange={(e) => setField(field.key, e.target.value)}
            />
          </label>
        ))}
        <label className="block lg:col-span-2">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs uppercase tracking-wide text-gray-400">Approval Mode</span>
            {editedAt.approvalMode && <span className="text-[10px] text-gray-500">{formatEditDate(editedAt.approvalMode)}</span>}
          </div>
          <p className="text-[11px] text-gray-500 mb-2">Manual = you review every action before PEPai executes. Autopilot = PEPai acts on your behalf within your rules.</p>
          <select
            className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
            value={profile.approvalMode}
            disabled={isDisabled}
            onChange={(e) => setField('approvalMode', e.target.value === 'autopilot' ? 'autopilot' : 'manual')}
          >
            <option value="manual">Manual Approval</option>
            <option value="autopilot">Autopilot</option>
          </select>
        </label>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          disabled={!canSave}
          onClick={onSave}
        >
          {saving ? 'Saving...' : 'Save P.E.P.ai Profile'}
        </button>
        {loading && <span className="text-sm text-gray-400">Loading profile...</span>}
        {notice && <span className="text-sm text-green-400">{notice}</span>}
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>
    </div>
  );
}
